import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ScheduleGrid, ScheduleLegend } from "@/components/ScheduleGrid";
import { DateStrip } from "@/components/DateStrip";
import { api, toDateString, formatHour } from "@/lib/api";
import { SITE, SITE_STUDIO_TYPE } from "@/config";
import { CalendarDays, ArrowRight } from "lucide-react";

export default function Planning() {
  const studio = SITE_STUDIO_TYPE;
  const [date, setDate] = useState(toDateString(new Date()));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHour, setSelectedHour] = useState(null);

  const fetchAvailability = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/availability", { params: { studio_type: studio, date } });
      setSlots(res.data.slots);
    } catch (e) {
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [studio, date]);

  useEffect(() => {
    fetchAvailability();
    setSelectedHour(null);
  }, [fetchAvailability]);

  const now = new Date();
  const isToday = date === toDateString(now);
  const freeCount = slots.filter(
    (s) => s.status === "libre" && (!isToday || s.hour > now.getHours())
  ).length;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <div className="flex flex-col gap-2">
        <h1 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
          Planning <span className="text-primary">{SITE.fullName}</span>
        </h1>
        <p className="text-muted-foreground">
          Consultez les disponibilités en créneaux d'une heure, 24h/24 – 7j/7.
        </p>
      </div>

      <div className="mt-6">
        <DateStrip selectedDate={date} onSelect={setDate} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px]">
        <div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground" data-testid="planning-date-label">
              <span className="font-medium capitalize text-foreground">
                {format(parseISO(date), "EEEE d MMMM yyyy", { locale: fr })}
              </span>
              {" · "}
              {loading ? "…" : `${freeCount} créneaux disponibles`}
            </p>
            <ScheduleLegend />
          </div>
          {loading ? (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
              {Array.from({ length: 24 }).map((_, i) => (
                <Skeleton key={i} className="h-[58px] rounded-md" />
              ))}
            </div>
          ) : (
            <ScheduleGrid
              slots={slots}
              date={date}
              selectedHours={selectedHour !== null ? [selectedHour] : []}
              onSlotClick={(hour) => setSelectedHour(hour === selectedHour ? null : hour)}
            />
          )}
        </div>

        <div className="lg:sticky lg:top-24 lg:self-start">
          <Card className="border-border bg-card" data-testid="planning-side-panel">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center justify-center rounded-lg bg-black py-2">
                <img
                  src={SITE.logo}
                  alt={`Logo ${SITE.fullName}`}
                  className="h-24 object-contain"
                  data-testid={`planning-logo-${studio}`}
                />
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold">{SITE.fullName}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{SITE.subtitle}</p>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium capitalize">
                    {format(parseISO(date), "EEE d MMM", { locale: fr })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Créneau</span>
                  <span className="font-mono-nums font-medium" data-testid="planning-selected-hour">
                    {selectedHour !== null ? `${formatHour(selectedHour)} – ${formatHour(selectedHour + 1)}` : "—"}
                  </span>
                </div>
              </div>
              <Button
                className="mt-6 w-full"
                disabled={selectedHour === null}
                asChild={selectedHour !== null}
                data-testid="planning-book-slot-button"
              >
                {selectedHour !== null ? (
                  <Link to={`/reserver?studio=${studio}&date=${date}&hour=${selectedHour}`}>
                    Réserver ce créneau
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                ) : (
                  <span>
                    <CalendarDays className="mr-2 inline h-4 w-4" />
                    Sélectionnez un créneau
                  </span>
                )}
              </Button>
              <p className="mt-4 text-xs text-muted-foreground">
                Astuce : payez en ligne à l'avance pour confirmer immédiatement votre créneau.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
